import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useParams } from 'react-router-dom';
import { usePersonalBrandingPillars } from '../hooks/usePersonalBrandingPillars';
import { PillarPageData } from '../hooks/usePillarsPageData';
import { useFilterContext } from '../context/FilterContext';
import { SectionCard, SectionHeader, SectionLoading, LegendItem } from './ui/Section';
import { useChartStyles, ChartTooltipCard } from './ui/chartStyles';

// Fallback color palette for pillars if FilterContext colors aren't available
const FALLBACK_PILLAR_COLORS = [
  '#BF6E50', // Religion (brown-red)
  '#8CB369', // Sports (green)
  '#D96E48', // Institutional (orange)
  '#D98E48', // History (orange-brown)
  '#B58CB3', // DEI (purple)
  '#ECBF68', // Engineering (yellow)
  '#D9A679', // Music (tan)
  '#56A3A6', // Science (teal)
  '#BFB3A8', // Aeronautics (light gray)
  '#47A363', // Innovation (green)
  '#A6923A', // Sustainability (olive)
  '#5E5E5E', // Law (dark gray)
];

interface MonthData {
  month_year: string;
  sum_engagement: number;
  avg_engagement: number;
  post_count: number;
}

interface EngagementByPillarProps {
  selectedPillars: string[];
  selectedBrands: string[];
  startDate: string | null;
  endDate: string | null;
}

const EngagementByPillar: React.FC<EngagementByPillarProps> = ({
  selectedPillars = [],
  selectedBrands = [],
  startDate = null,
  endDate = null,
}) => {
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  
  // Fetch data using our custom hook
  const { data, loading, error } = usePersonalBrandingPillars(
    orgId,
    selectedPillars,
    startDate,
    endDate,
    selectedBrands
  );
  
  // Get pillar colors from FilterContext
  const { pillarColors, pillarColorsLoading } = useFilterContext();
  const cs = useChartStyles();
  
  // Function to get pillar color (from FilterContext or fallback)
  const getPillarColor = (pillar: string, index: number) => {
    if (!pillarColorsLoading && pillarColors[pillar]) {
      return pillarColors[pillar];
    }
    return FALLBACK_PILLAR_COLORS[index % FALLBACK_PILLAR_COLORS.length];
  };
  
  // Determine which pillars to display in the chart
  const pillarsToDisplay = useMemo(() => {
    if (!data) return [];
    
    // If specific pillars are selected, use those
    if (selectedPillars && selectedPillars.length > 0) {
      return selectedPillars;
    }
    
    // Otherwise, use all pillars from the data
    return data.pillars || [];
  }, [data, selectedPillars]);

  // Process data for the chart - calculating engagement percentages by month
  const chartData = useMemo(() => {
    if (!data || !data.monthly_pillar_metrics) return [];
    
    // Get all unique months across all pillars
    const allMonths = new Set<string>();
    
    // First pass - collect all months and brands
    Object.entries(data.monthly_pillar_metrics).forEach(([pillarName, monthList]) => {
      if (pillarsToDisplay.includes(pillarName)) {
        const typedMonthList = monthList as MonthData[];
        typedMonthList.forEach((monthData: MonthData) => {
          allMonths.add(monthData.month_year);
        });
      }
    });
    
    // Convert to array and sort chronologically
    const sortedMonths = Array.from(allMonths).sort();
    
    // Second pass - create data for the chart
    const processedData = sortedMonths.map(month => {
      // Start with the month as the base object
      const monthData: { [key: string]: any } = { month_year: month };
      
      // Track total engagement for percentage calculation
      let totalMonthEngagement = 0;
      
      // First calculate total engagement for this month across all displayed pillars
      pillarsToDisplay.forEach((pillar: string) => {
        const pillarMonthData = (data.monthly_pillar_metrics[pillar] as MonthData[] | undefined)?.find(
          (item: MonthData) => item.month_year === month
        );
        
        if (pillarMonthData) {
          totalMonthEngagement += pillarMonthData.sum_engagement;
        }
      });
      
      // Now calculate percentage for each pillar
      pillarsToDisplay.forEach((pillar: string) => {
        const pillarMonthData = (data.monthly_pillar_metrics[pillar] as MonthData[] | undefined)?.find(
          (item: MonthData) => item.month_year === month
        );
        
        if (pillarMonthData && totalMonthEngagement > 0) {
          // Calculate percentage of total engagement
          const percentage = (pillarMonthData.sum_engagement / totalMonthEngagement) * 100;
          // Ensure we have a clean number and round to 1 decimal place
          const cleanPercentage = Math.max(0, Math.min(100, percentage)); // Clamp between 0-100
          monthData[pillar] = Math.round(cleanPercentage * 10) / 10; // Round to 1 decimal place
        } else {
          monthData[pillar] = 0;
        }
      });
      
      return monthData;
    });
    
    return processedData;
  }, [data, pillarsToDisplay]);

  // Create tooltip formatter
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const rows = [...payload]
        .filter((entry: any) => entry.value > 0)
        .sort((x: any, y: any) => y.value - x.value)
        .map((entry: any, index: number) => ({
          key: `item-${index}`,
          color: entry.color,
          label: entry.name,
          value: `${entry.value.toFixed(1)}%`,
        }));
      return <ChartTooltipCard title={formatXAxisTick(label)} rows={rows} />;
    }
    return null;
  };

  // Format X-axis tick for better display
  const formatXAxisTick = (tickItem: string) => {
    // Convert "YYYY-MM" to "Mon YYYY"
    const parts = tickItem.split('-');
    if (parts.length >= 2) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = parseInt(parts[1]) - 1; // 0-based index
      return `${months[monthIndex]} ${parts[0]}`;
    }
    return tickItem;
  };

  // Calculate bar size based on number of months
  const getBarSize = () => {
    const barCount = chartData.length;
    if (barCount <= 6) return 40;
    if (barCount <= 12) return 30;
    if (barCount <= 24) return 20;
    return 15;
  };

  const header = (
    <SectionHeader
      title="Engagement by Pillar"
      info="Each month's total engagement split across content pillars, normalized to 100% so you can compare each pillar's share over time."
    />
  );

  // Loading state
  if (loading && !data) {
    return (
      <SectionCard>
        {header}
        <SectionLoading height={360} />
      </SectionCard>
    );
  }

  // Error state
  if (error) {
    return (
      <SectionCard>
        {header}
        <Alert severity="error">Pillar data didn't load: {error}</Alert>
      </SectionCard>
    );
  }

  // No data state
  if (!data || chartData.length === 0) {
    return (
      <SectionCard>
        {header}
        <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
          No pillar engagement for these filters.
        </Typography>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      {header}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1, mb: 2.5 }}>
        {pillarsToDisplay.map((pillar: string, index: number) => (
          <LegendItem key={pillar} color={getPillarColor(pillar, index)} label={pillar} />
        ))}
      </Box>

      <Box sx={{ width: '100%', height: 360 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
            barSize={getBarSize()}
          >
            <CartesianGrid {...cs.grid} />
            <XAxis
              {...cs.xAxis}
              dataKey="month_year"
              tickFormatter={formatXAxisTick}
              minTickGap={12}
            />
            <YAxis
              {...cs.yAxis}
              width={64}
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              allowDataOverflow
              label={cs.axisLabel('% of total engagement')}
              tickFormatter={(value) => {
                // Handle floating point precision issues
                const numValue = typeof value === 'number' ? value : parseFloat(value);
                if (isNaN(numValue)) return '0%';
                // Round to avoid floating point display issues
                const rounded = Math.round(numValue * 100) / 100;
                return `${rounded}%`;
              }}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={cs.cursor}
            />
            {pillarsToDisplay.map((pillar: string, index: number) => (
              <Bar
                key={pillar}
                dataKey={pillar}
                stackId="stack"
                fill={getPillarColor(pillar, index)}
                name={pillar}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </SectionCard>
  );
};

export default EngagementByPillar; 